import React, { CSSProperties, useMemo } from 'react';
import { Controller, Control, FieldValues } from 'react-hook-form';
import {
  FormControl,
  Select,
  SelectProps,
  MenuItem,
  Stack,
  Checkbox,
  ListItemText,
  FormControlLabel,
  SelectChangeEvent,
  Typography,
} from '@mui/material';

import TextFormLabel from '../text/TextFormLabel';
import TextFormError from '../text/TextFormError';
import { useLocalization } from '../../../localization';
import CaretDownIcon from '../icons/CaretDownIcon';

export interface SelectOption {
  label: string;
  labelElement?: React.ReactNode;
  value: string | number;
  disabled?: boolean;
}

type SelectValue = (string | number)[];

interface MultiSelectProps<T extends FieldValues = FieldValues> extends Omit<SelectProps, 'onChange' | 'value' | 'defaultValue'> {
  name: string;
  control: Control<T>;
  options: SelectOption[];
  label: string;
  required?: boolean;
  defaultValue?: SelectValue;
  disabled?: boolean;
  loading?: boolean;
  style?: CSSProperties;
  menuHeight?: number;
  onChange?: (value: SelectValue) => void;
}

export const InputMultiSelectField = <T extends FieldValues = FieldValues>({
  name,
  control,
  options,
  label,
  required = false,
  defaultValue = [],
  disabled = false,
  loading = false,
  onChange,
  style,
  menuHeight = 300,
  ...selectProps
}: MultiSelectProps<T>) => {
  const { t } = useLocalization();
  const allValues = useMemo(() => options.filter(opt => !opt.disabled).map(opt => opt.value), [options]);

  return (
    <Stack spacing={0.5}>
      <TextFormLabel label={t(label)} required={required} />
      <Controller
        name={name as any}
        control={control}
        defaultValue={defaultValue as any}
        render={({ field: { value, onChange: fieldOnChange, onBlur: fieldOnBlur }, fieldState: { error } }) => {
          const selectedValues: SelectValue = Array.isArray(value) ? value : [];

          const isAllSelected =
            allValues.length > 0 &&
            allValues.length === selectedValues.length &&
            allValues.every(v => selectedValues.includes(v));

          const handleSelectAllToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
            const checked = event.target.checked;
            const newValues = checked ? allValues : [];
            fieldOnChange(newValues);
            onChange?.(newValues);
          };

          const handleChange = (event: SelectChangeEvent<unknown>) => {
            const vals = event.target.value as SelectValue;
            fieldOnChange(vals);
            onChange?.(vals);
          };

          return (
            <FormControl
              variant="outlined"
              fullWidth
              error={Boolean(error)}
              disabled={disabled || loading}
              style={style}
            >
              <Select
                multiple
                value={selectedValues}
                onBlur={fieldOnBlur}
                onChange={handleChange}
                renderValue={selected => selected.map(val => options.find(o => o.value === val)?.label).join(', ')}
                MenuProps={{ PaperProps: { style: { maxHeight: menuHeight } } }}
                IconComponent={props => <CaretDownIcon {...props} style={{ marginRight: '0.5rem' }} />}
                {...selectProps}
                size="small"
              >
                <MenuItem disableRipple>
                  <FormControlLabel
                    control={<Checkbox checked={isAllSelected} onChange={handleSelectAllToggle} size="small" />}
                    label={
                      <Typography variant="h5" sx={{ fontWeight: 550 }}>
                        {t('common.terms.selectAll')}
                      </Typography>
                    }
                    sx={{ width: '100%', margin: 0, pl: 0 }}
                    onClick={event => event.stopPropagation()}
                  />
                </MenuItem>
                {options.map(opt => {
                  const isChecked = selectedValues.includes(opt.value);
                  return (
                    <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                      <Checkbox checked={isChecked} disabled={opt.disabled} size="small" />
                      {opt.labelElement ? opt.labelElement : <ListItemText primary={t(opt.label)} />}
                    </MenuItem>
                  );
                })}
              </Select>
              <TextFormError error={error} />
            </FormControl>
          );
        }}
      />
    </Stack>
  );
};
